import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Spin,
  Pagination,
} from "antd";
import API, { GAMING_HIS, GAMING_HIS_EXCEL } from "../../network/api";
import moment from "moment";
import "moment-timezone";
import { TEXT_DEF } from "../../constant/text";
import { VIP_LEVELS, VIP_ORDER } from "../../constant/vip";
import {
  defaultDayRange,
  PICKER_DATE_FORMAT,
  pickerFromDisabled,
  pickerToDisabled,
  ymdFromPicker,
} from "../../constant/searchRange";

const TEXT = {
  score: "Số điểm nhận được",
  time: "Thời gian",
  status: "Trạng thái",
  vip: "Cấp VIP",
};

const PLAY_RESULT_LABEL = {
  WIN: "Thành công",
  BOOM: "Thành công",
  ABANDONED: "Bỏ cuộc",
  TIMEOUT: "Quá thời gian",
};

function mapPlayResult(result) {
  if (result == null || result === "") return "";
  const key = String(result).trim().toUpperCase();
  return PLAY_RESULT_LABEL[key] || String(result).trim();
}

/**
 * @typedef {Object} Summary
 * @property {number} totalPlays
 * @property {number} totalUsers
 * @property {number} totalScore
 */

const GamingHistoryList = () => {
  const [form] = Form.useForm();
  const [, forceUpdate] = useState({});
  const dayRange = defaultDayRange();
  const [dateFrom, setDateFrom] = useState(dayRange.fromYmd);
  const [dateTo, setDateTo] = useState(dayRange.toYmd);
  const [dataList, setDataList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState(dayRange.query);
  const [summary, setSummary] = useState({
    totalPlays: 0,
    totalUsers: 0,
    totalScore: 0,
  });

  const locale = { emptyText: TEXT_DEF.text0035 };
  const usernameFilterLocal = localStorage.getItem("usernameFilter");

  useEffect(() => {
    forceUpdate({});
    const range = defaultDayRange();
    form.setFieldsValue({
      dateFrom: range.fromDayjs,
      dateTo: range.toDayjs,
    });
    if (usernameFilterLocal) {
      setDataFilter(`username=${usernameFilterLocal}&${range.query}`);
      form.setFieldValue("usernameFilter", usernameFilterLocal);
      localStorage.removeItem("usernameFilter");
    }
  }, [form, usernameFilterLocal]);

  useEffect(() => {
    getListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dataFilter]);

  const getListData = async () => {
    setLoading(true);
    try {
      const resp = await API.get(
        `${GAMING_HIS}?pageSize=10&page=${page}${dataFilter ? `&${dataFilter}` : ""}`
      );

      const payload = resp?.data?.data;
      const list = payload?.data ?? [];
      const mapped = list.map((item, index) => ({
        key: item?.id ?? `${item?.username || "play"}-${page}-${index}`,
        stt: item?.stt,
        username: item?.username,
        createdAt: item?.createdAt,
        score: Number(item?.score ?? 0),
        status: mapPlayResult(item?.result),
        vipLevel: item?.vipLevel ?? "",
        boom: item?.result === "BOOM" ? "Trúng boom" : "",
      }));

      setDataList(mapped);
      setTotalRecord(Number(payload?.total ?? mapped.length) || 0);

      // ƯU TIÊN dùng summary từ API; nếu không có -> fallback từ trang hiện tại
      const s = payload?.summary;
      if (s && (typeof s.totalPlays === "number" || typeof s.totalScore === "number" || typeof s.totalUsers === "number")) {
        setSummary({
          totalPlays: Number(s.totalPlays || 0),
          totalUsers: Number(s.totalUsers || 0),
          totalScore: Number(s.totalScore || 0),
        });
      } else {
        const totalScore = mapped.reduce((acc, cur) => acc + (cur.score || 0), 0);
        const totalUsers = new Set(mapped.map((x) => x.username)).size;
        const totalPlays = Number(payload?.total ?? mapped.length) || 0;
        setSummary({ totalPlays, totalUsers, totalScore });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onChangePage = (p) => setPage(p);

  const onSearch = () => {
    let params = "";
    if (form.getFieldValue("usernameFilter"))
      params += `username=${form.getFieldValue("usernameFilter").trim().toLowerCase()}`;
    if (form.getFieldValue("userStatus"))
      params += params !== "" ? `&status=${form.getFieldValue("userStatus")}` : `status=${form.getFieldValue("userStatus")}`;
    if (form.getFieldValue("vipFilter"))
      params += params !== "" ? `&vipLevel=${form.getFieldValue("vipFilter")}` : `vipLevel=${form.getFieldValue("vipFilter")}`;
    if (dateFrom) params += params !== "" ? `&fromDate=${dateFrom}` : `fromDate=${dateFrom}`;
    if (dateTo) {
      params += params !== "" ? `&toDate=${dateTo}` : `toDate=${dateTo}`;
      if (!dateFrom) params += `&fromDate=${dayRange.fromYmd}`;
    }

    setPage(1);
    setDataFilter(params !== "" ? params : null);
  };

  const dataColums = [
    { title: "STT", dataIndex: "stt", key: "stt" },
    { title: TEXT_DEF.text0002, dataIndex: "username", key: "username" },
    {
      title: TEXT.vip,
      dataIndex: "vipLevel",
      key: "vipLevel",
      sorter: (a, b) =>
        (VIP_ORDER[a.vipLevel] ?? 999) - (VIP_ORDER[b.vipLevel] ?? 999),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: TEXT.score,
      key: "score",
      dataIndex: "score",
      sorter: (a, b) => (a.score ?? 0) - (b.score ?? 0),
    },
    {
      title: "Boom",
      key: "boom",
      dataIndex: "boom",
    },
    {
      title: "Thời gian",
      key: "createdAt",
      render: (record) =>
        moment(record?.createdAt).utcOffset(7).format("DD-MM-YYYY HH:mm:ss"),
    },
    { title: TEXT.status, key: "status", dataIndex: "status" },
  ];

  const onChangeDate = (value, type) => {
    if (value) {
      if (type === "from") setDateFrom(ymdFromPicker(value, false));
      else setDateTo(ymdFromPicker(value, true));
    } else if (type === "from") {
      setDateFrom(null);
    } else {
      setDateTo(null);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const qs = dataFilter ? `?${dataFilter}` : "";
      const response = await API.post(
        `${GAMING_HIS_EXCEL}${qs}`,
        {},
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers?.["content-disposition"]; // axios: dùng indexer
      let fileName = "lich_su_choi_game.xlsx";
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].replace(/"/g, "");
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const renderHistory = () => {
    return (
      <>
        <div className="col-12">
          <Form form={form} layout="inline" autoComplete="off" onFinish={onSearch}>
            <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
              <div className="col-5">
                <Form.Item name="usernameFilter" label={TEXT_DEF.text0002}>
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <div className="col-5">
                <Form.Item name="vipFilter" label="Cấp VIP">
                  <select
                    style={{
                      width: "100%",
                      height: 32,
                      borderRadius: 6,
                      border: "1px solid #d9d9d9",
                      paddingLeft: 8,
                    }}
                  >
                    <option value="">Tất cả</option>
                    {VIP_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </Form.Item>
              </div>
            </div>

            <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
              <div className="col-5">
                <Form.Item name="dateFrom" label={TEXT_DEF.text0032}>
                  <DatePicker
                    placeholder=""
                    onChange={(value) => onChangeDate(value, "from")}
                    format={PICKER_DATE_FORMAT}
                    style={{ width: "100%" }}
                    disabledDate={pickerFromDisabled(dateTo)}
                  />
                </Form.Item>
              </div>
              <div className="col-5">
                <Form.Item name="dateTo" label={TEXT_DEF.text0033}>
                  <DatePicker
                    placeholder=""
                    onChange={(value) => onChangeDate(value, "to")}
                    format={PICKER_DATE_FORMAT}
                    style={{ width: "100%" }}
                    disabledDate={pickerToDisabled(dateFrom)}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="payment-search">
              <Button htmlType="submit" type="primary">
                {TEXT_DEF.text0031}
              </Button>
              <Button
                style={{ marginLeft: 20 }}
                onClick={handleDownloadExcel}
                htmlType="button"
                type="primary"
              >
                {TEXT_DEF.text0050}
              </Button>
            </div>
          </Form>
        </div>

        {/* 3 tổng số — ngay sau tìm kiếm & trước bảng */}
        <div
          className="col-12"
          style={{
            display: "flex",
            gap: 40,
            margin: "12px 0 8px",
            fontWeight: 600,
            fontSize: 15,
            flexWrap: "wrap",
          }}
        >
          <div>Tổng số lượt chơi: {summary.totalPlays}</div>
          <div>Tổng số lượng HV tham gia: {summary.totalUsers}</div>
          <div>Tổng điểm thưởng đã nhận: {summary.totalScore}</div>
        </div>

        <Spin spinning={loading} delay={500} size="large">
          <div style={{ display: "flex", width: "100%", marginTop: 10 }}>
            <Table
              key="gamingHistory"
              locale={locale}
              columns={dataColums}
              dataSource={dataList}
              pagination={false}
              bordered
              style={{ width: "100%" }}
            />
          </div>
          <div
            style={{
              display: "flex",
              width: "100%",
              marginTop: 15,
              justifyContent: "flex-end",
            }}
          >
            <Pagination
              current={page}
              total={totalRecord}
              onChange={onChangePage}
              defaultPageSize={10}
              showSizeChanger={false}
              showTotal={(t) => `Tổng cộng ${t} dữ liệu`}
            />
          </div>
        </Spin>
      </>
    );
  };

  return <div className="col-12">{renderHistory()}</div>;
};

export default GamingHistoryList;
